import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./ProductsPage.css";
import Footer from "./footer";
import Spinner from "../components/Spinner";
import { addToGuestCart } from "./CartService";

export default function ProductsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname.startsWith("/products/")
    ? location.pathname.replace("/products/", "")
    : "";
  const [products, setProducts] = useState([]);
  const [categoryTitle, setCategoryTitle] = useState("محصولات ما");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [wishlistIds, setWishlistIds] = useState([]);

  const token = localStorage.getItem("token");
  const API_BASE = process.env.REACT_APP_API_URL || "https://api.goharesadaf.ir";

  // Load products
  useEffect(() => {
    setLoading(true);
    setError(null);
    const apiEndpoint = path
      ? `${API_BASE}/api/products/categories/${path}`
      : `${API_BASE}/api/products/all-products`;

    fetch(apiEndpoint)
      .then((res) => {
        if (!res.ok) {
          if (res.status === 404) throw new Error("دسته‌بندی یافت نشد یا آدرس نامعتبر است.");
          throw new Error("خطا در دریافت محصولات!");
        }
        return res.json();
      })
      .then((data) => {
      if (data && typeof data === "object" && !Array.isArray(data)) {
        if (data.products) {
          // shape A
          setProducts(data.products);
          // set page title from category data, prefer breadcrumb's last segment, fallback to name:
          if (data.category) {
            const bc = data.category.breadcrumb || data.category.name || "";
            const parts = bc.split(">").map(p => p.trim()).filter(Boolean);
            const last = parts.length ? parts[parts.length - 1] : (data.category.name || "محصولات ما");
            setCategoryTitle(last);
          } else {
            setCategoryTitle("محصولات ما");
          }
        } else if (Array.isArray(data.data)) {
          // paginated shape: { data: [...], ... }
          setProducts(data.data);
          // try to get category from first item if available:
          const firstCat = data.data[0]?.category;
          if (firstCat) {
            const bc = firstCat.breadcrumb || firstCat.name || "";
            const parts = bc.split(">").map(p => p.trim()).filter(Boolean);
            setCategoryTitle(parts.length ? parts[parts.length - 1] : (firstCat.name || "محصولات ما"));
          } else {
            setCategoryTitle("محصولات ما");
          }
        } else {
          // unknown object shape — maybe the backend returned an array inside the object
          const arr = data.products || data.items || [];
          if (Array.isArray(arr) && arr.length) {
            setProducts(arr);
            const firstCat = arr[0].category;
            if (firstCat) {
              const bc = firstCat.breadcrumb || firstCat.name || "";
              const parts = bc.split(">").map(p => p.trim()).filter(Boolean);
              setCategoryTitle(parts.length ? parts[parts.length - 1] : (firstCat.name || "محصولات ما"));
            } else {
              setCategoryTitle("محصولات ما");
            }
          } else {
            // fallback: maybe data is a single product or empty
            setProducts(Array.isArray(data) ? data : []);
            setCategoryTitle("محصولات ما");
          }
        }
      } else if (Array.isArray(data)) {
        // shape C: plain array
        setProducts(data);
        // prefer category from path: if the path exists, try to parse it to readable title:
        if (path) {
          // we can take last segment of the path as fallback title (but better if backend returns category name)
          const segs = path.split("/").filter(Boolean);
          const lastSeg = segs.length ? segs[segs.length-1] : "";
          // lastSeg is slug; optionally convert slug-to-name on the server. Fallback to "محصولات ما"
          setCategoryTitle(decodeURIComponent(lastSeg.replace(/-/g, " ")) || "محصولات ما");
        } else {
          setCategoryTitle("محصولات ما");
        }
      } else {
        // unexpected shape
        setProducts([]);
        setCategoryTitle("محصولات ما");
      }
    })
    .catch((err) => {
      setError(err.message);
      setProducts([]);
      setCategoryTitle("محصولات ما");
    })
    .finally(() => setLoading(false));
}, [path]);

  // Load user's wishlist IDs
  useEffect(() => {
    if (!token) return;

    fetch(`${API_BASE}/api/wishlist`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        // Keep only IDs
        const ids = data.map((p) => p.id);
        setWishlistIds(ids);
      })
      .catch((err) => console.error("Failed to load wishlist", err));
  }, [token]);

  const handleAddToCart = async (e, product) => {
    e.stopPropagation();
    if (!token) {
      addToGuestCart(product, 1);
      alert("محصول به سبد خرید اضافه شد!");
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/cart/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ product_id: product.id, quantity: 1 }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "خطا در افزودن به سبد خرید");
      }

      alert("محصول به سبد خرید اضافه شد!");
    } catch (err) {
      alert(err.message);
    }
  };

  const toggleWishlist = async (e, productId) => {
    e.stopPropagation();
    if (!token) {
      alert("لطفاً ابتدا وارد حساب کاربری شوید.");
      navigate("/auth");
      return;
    }

    const inWishlist = wishlistIds.includes(productId);
    const method = inWishlist ? "DELETE" : "POST";

    try {
      const res = await fetch(`${API_BASE}/api/wishlist/${productId}`, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("خطا در بروزرسانی علاقه‌مندی‌ها");

      // Update local wishlist IDs
      setWishlistIds((prev) =>
        inWishlist ? prev.filter((id) => id !== productId) : [...prev, productId]
      );
    } catch (err) {
      alert(err.message);
    }
  };

const breadcrumb = products.length > 0
  ? (() => {
      const bc = products[0].category?.breadcrumb || "";
      const parts = bc.split(">").map(p => p.trim());
      return parts[parts.length - 1] || "محصولات ما";
    })()
  : "محصولات ما";

  return (
    <div className="products-page" style={{ cursor: "url('/images/shell.png'), auto" }}>
      <div className="content-wrapper">
        <section className="products-hero">
          <h1 className="page-title">
            <span className="breadcrumb-box">{categoryTitle}</span>
          </h1>
        </section>

        {loading && <Spinner />}
        {error && <p>خطا در بارگذاری: {error}</p>}

        {!loading && !error && (
          <section className="ProductsPageProductGrid">
            {products.length === 0 && <p>هیچ محصولی یافت نشد.</p>}
            {products.map((product) => {
              const inWishlist = wishlistIds.includes(product.id);
              const discountedPrice =
              product.on_sale && product.discount_percentage > 0
                ? product.price * (1 - product.discount_percentage / 100)
                : null;
              return (
                <div
                  className="ProductsPageProductCard"
                  key={product.id}
                  onClick={() => navigate(`/product/${product.id}`)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="image-wrapper">
                    <img
                      src={product.main_image || "/images/placeholder.png"}
                      alt={product.title}
                    />
                  </div>
                  <h3 className="ProductsPageDescription">{product.title}</h3>
                  <p className="ProductsPagePrice">
                    {product.price ? (
                      product.on_sale && product.discount_percentage > 0 ? (
                        <>
                          <span className="old-price">
                            {parseFloat(product.price).toLocaleString("fa-IR")} تومان
                          </span>
                          <span className="new-price">
                            {parseFloat(
                              product.price * (1 - product.discount_percentage / 100)
                            ).toLocaleString("fa-IR")}{" "}
                            تومان
                          </span>
                        </>
                      ) : (
                        <span className="regular-price">
                          {parseFloat(product.price).toLocaleString("fa-IR")} تومان
                        </span>
                      )
                    ) : (
                      <button
                        className="price-btn"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/contact?product=${product.id}`);
                        }}
                      >
                        اطلاع از قیمت
                      </button>
                    )}
                  </p>

                  
                  {product.price && (
                  <button
                    className="add-to-cart"
                    disabled={!product.active || product.stock < 1}
                    title={
                      product.active && product.stock > 0
                        ? "افزودن به سبد خرید"
                        : "ناموجود"
                    }
                    onClick={(e) => handleAddToCart(e, product)}
                  >
                    افزودن به سبد خرید
                  </button>
                  )}


                  <button
                    className={`wishlist-btn ${inWishlist ? "active" : ""}`}
                    onClick={(e) => toggleWishlist(e, product.id)}
                    aria-pressed={inWishlist}
                    title={inWishlist ? "حذف از علاقه‌مندی‌ها" : "افزودن به علاقه‌مندی‌ها"}
                  >
                    <span className="star" aria-hidden="true">{inWishlist ? "❤️" : "🤍"}</span>
                    <span className="sr-only">{inWishlist ? "حذف از علاقه‌مندی‌ها" : "افزودن به علاقه‌مندی‌ها"}</span>
                  </button>
                  {product.discount_percentage > 0 && (
                    <div className="discount-badge">
                     % {product.discount_percentage.toLocaleString("fa-IR")}
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        )}
      </div>
      <Footer />
    </div>
  );
}
